import React, { useEffect, useRef, useState } from "react";
import { apiUrl } from "../configs/api";
import classes from "./GeneralFeedbackModal.module.css";

const FEEDBACK_MAX_LENGTH = 4000;
const FEEDBACK_MIN_LENGTH = 5;
const NAME_MAX_LENGTH = 150;
const EMAIL_MAX_LENGTH = 254;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const SUCCESS_CLOSE_DELAY_MS = 1200;

const initialErrors = {
  feedback: "",
  name: "",
  email: "",
};

function validateFeedbackForm({ feedback, name, email }) {
  const errors = { ...initialErrors };
  const trimmedFeedback = feedback.trim();
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();

  if (!trimmedFeedback) {
    errors.feedback = "Please enter your feedback.";
  } else if (trimmedFeedback.length < FEEDBACK_MIN_LENGTH) {
    errors.feedback = "Feedback must be at least 5 characters.";
  } else if (trimmedFeedback.length > FEEDBACK_MAX_LENGTH) {
    errors.feedback = "Feedback must be 4000 characters or less.";
  }

  if (trimmedName.length > NAME_MAX_LENGTH) {
    errors.name = "Name must be 150 characters or less.";
  }

  if (trimmedEmail.length > EMAIL_MAX_LENGTH) {
    errors.email = "Email must be 254 characters or less.";
  } else if (trimmedEmail && !EMAIL_PATTERN.test(trimmedEmail)) {
    errors.email = "Please enter a valid email address.";
  }

  return {
    errors,
    valid: !errors.feedback && !errors.name && !errors.email,
  };
}

const GeneralFeedbackModal = ({ open, onClose }) => {
  const [feedback, setFeedback] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState(initialErrors);
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const modalRef = useRef(null);
  const textareaRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    setFeedback("");
    setName("");
    setEmail("");
    setErrors(initialErrors);
    setSubmitStatus("idle");
    setSubmitMessage("");

    const focusTimer = window.setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) {
        window.clearTimeout(closeTimerRef.current);
      }
    };
  }, []);

  if (!open) return null;

  const handleClose = () => {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    onClose?.();
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      handleClose();
      return;
    }

    if (event.key !== "Tab") return;

    const focusableElements = modalRef.current?.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    );
    const focusable = Array.from(focusableElements || []);
    if (!focusable.length) return;

    const firstElement = focusable[0];
    const lastElement = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const clearFieldError = (fieldName) => {
    if (!errors[fieldName]) return;
    setErrors((currentErrors) => ({
      ...currentErrors,
      [fieldName]: "",
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage("");

    const validation = validateFeedbackForm({ feedback, name, email });
    setErrors(validation.errors);

    if (!validation.valid || submitStatus === "submitting") return;

    const payload = {
      feedback: feedback.trim(),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
    };

    setSubmitStatus("submitting");

    try {
      const response = await fetch(apiUrl("/api/feedback/general"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        setSubmitStatus("error");
        setSubmitMessage(
          response.status >= 400 && response.status < 500
            ? "Please check your feedback and try again."
            : "We could not submit your feedback. Please try again.",
        );
        return;
      }

      setFeedback("");
      setName("");
      setEmail("");
      setErrors(initialErrors);
      setSubmitStatus("success");
      setSubmitMessage("Thank you for your feedback.");
      closeTimerRef.current = window.setTimeout(() => {
        onClose?.();
      }, SUCCESS_CLOSE_DELAY_MS);
    } catch (error) {
      setSubmitStatus("error");
      setSubmitMessage("We could not submit your feedback. Please try again.");
    }
  };

  const isSubmitting = submitStatus === "submitting";

  return (
    <div className={classes.overlay} onClick={handleClose}>
      <div
        ref={modalRef}
        className={classes.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="general-feedback-title"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        <button
          type="button"
          className={classes.close}
          aria-label="Close feedback form"
          onClick={handleClose}
        >
          &times;
        </button>

        <h3 id="general-feedback-title" className={classes.title}>
          Send feedback
        </h3>

        <form className={classes.form} onSubmit={handleSubmit} noValidate>
          <section className={classes.section}>
            <label className={classes.label} htmlFor="general-feedback-text">
              Feedback
            </label>
            <textarea
              ref={textareaRef}
              id="general-feedback-text"
              className={`${classes.textarea}${
                errors.feedback ? ` ${classes.invalid}` : ""
              }`}
              value={feedback}
              onChange={(event) => {
                setFeedback(event.target.value);
                clearFieldError("feedback");
              }}
              placeholder="Tell us what you think or what we can improve."
              maxLength={FEEDBACK_MAX_LENGTH}
              rows={6}
              aria-required="true"
              aria-invalid={Boolean(errors.feedback)}
              aria-describedby={
                errors.feedback ? "general-feedback-error" : undefined
              }
              disabled={isSubmitting}
            />
            {errors.feedback && (
              <p
                id="general-feedback-error"
                className={classes.error}
                role="alert"
              >
                {errors.feedback}
              </p>
            )}
          </section>

          <section className={classes.section}>
            <div className={classes.group_header}>
              <h4 className={classes.group_title}>Contact details (optional)</h4>
              <p className={classes.helper}>
                If you want us to contact you, please provide your details.
              </p>
            </div>

            <div className={classes.field_grid}>
              <div className={classes.field}>
                <label className={classes.label} htmlFor="general-feedback-name">
                  Name
                </label>
                <input
                  id="general-feedback-name"
                  className={`${classes.input}${
                    errors.name ? ` ${classes.invalid}` : ""
                  }`}
                  type="text"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    clearFieldError("name");
                  }}
                  maxLength={NAME_MAX_LENGTH + 1}
                  aria-invalid={Boolean(errors.name)}
                  aria-describedby={
                    errors.name ? "general-feedback-name-error" : undefined
                  }
                  disabled={isSubmitting}
                />
                {errors.name && (
                  <p
                    id="general-feedback-name-error"
                    className={classes.error}
                    role="alert"
                  >
                    {errors.name}
                  </p>
                )}
              </div>

              <div className={classes.field}>
                <label className={classes.label} htmlFor="general-feedback-email">
                  Email
                </label>
                <input
                  id="general-feedback-email"
                  className={`${classes.input}${
                    errors.email ? ` ${classes.invalid}` : ""
                  }`}
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    clearFieldError("email");
                  }}
                  maxLength={EMAIL_MAX_LENGTH + 1}
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={
                    errors.email ? "general-feedback-email-error" : undefined
                  }
                  disabled={isSubmitting}
                />
                {errors.email && (
                  <p
                    id="general-feedback-email-error"
                    className={classes.error}
                    role="alert"
                  >
                    {errors.email}
                  </p>
                )}
              </div>
            </div>
          </section>

          {submitMessage && (
            <p
              className={
                submitStatus === "success" ? classes.success : classes.form_error
              }
              role={submitStatus === "success" ? "status" : "alert"}
            >
              {submitMessage}
            </p>
          )}

          <div className={classes.actions}>
            <button
              type="button"
              className={classes.secondary}
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={classes.primary}
              disabled={isSubmitting || submitStatus === "success"}
            >
              {isSubmitting ? "Submitting..." : "Submit feedback"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export { validateFeedbackForm };
export default GeneralFeedbackModal;
